import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BrandLockup } from "@/components/brand/BrandLockup";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { ROUTES } from "@/constants/routes";
import { usePublicAnalytics } from "@/hooks/usePublicAnalytics";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { href: "#product-proof", key: "product", placement: "header_product" },
  { href: "#progress-proof", key: "progress", placement: "header_progress" },
  { href: "#coach-proof", key: "coach", placement: "header_coach" },
  { href: "#pricing", key: "pricing", placement: "header_pricing" },
] as const;

const navLinkClassName =
  "inline-flex min-h-11 items-center text-sm font-medium text-white/70 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#101211]";

export function PublicHeader() {
  const { t } = useTranslation();
  const { track } = usePublicAnalytics();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const updateScrolledState = () => setIsScrolled(window.scrollY > 32);

    updateScrolledState();
    window.addEventListener("scroll", updateScrolledState, { passive: true });

    return () => window.removeEventListener("scroll", updateScrolledState);
  }, []);

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia("(min-width: 1024px)");
    const closeMenuAtDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setIsMenuOpen(false);
    };

    desktopMediaQuery.addEventListener("change", closeMenuAtDesktop);

    return () => desktopMediaQuery.removeEventListener("change", closeMenuAtDesktop);
  }, []);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 h-[72px] border-b border-transparent transition-colors duration-150",
        isScrolled && "border-white/10 bg-[#101211]",
      )}
    >
      <div className="mx-auto flex h-full max-w-[1280px] items-center justify-between px-4 md:px-8">
        <BrandLockup inverted />

        <div className="hidden items-center gap-6 lg:flex">
          <nav aria-label={t("common.main_navigation")} className="flex items-center gap-6">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={() => void track("public_nav_clicked", item.placement)}
                className={navLinkClassName}
              >
                {t(`frontpage.precision.nav.${item.key}`)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher inverted />
            <Link
              to={ROUTES.LOGIN}
              onClick={() => void track("public_nav_clicked", "header_login")}
              className={navLinkClassName}
            >
              {t("frontpage.precision.nav.login")}
            </Link>
            <Link
              to={ROUTES.SIGNUP}
              onClick={() => void track("public_cta_clicked", "header_signup")}
              className="inline-flex h-11 items-center justify-center bg-brand px-5 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-brand-700 hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#101211]"
            >
              {t("frontpage.precision.nav.signup")}
            </Link>
          </div>
        </div>

        <Dialog.Root
          open={isMenuOpen}
          onOpenChange={(nextOpen) => {
            setIsMenuOpen(nextOpen);
            if (nextOpen) void track("public_menu_opened", "mobile_header");
          }}
        >
          <Dialog.Trigger asChild>
            <button
              type="button"
              aria-label={t("frontpage.precision.nav.menu_open")}
              className="inline-flex h-11 w-11 items-center justify-center text-white transition-colors duration-150 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white lg:hidden"
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          </Dialog.Trigger>

          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[60] bg-[#101211]" />
            <Dialog.Content className="fixed inset-0 z-[61] flex min-h-dvh flex-col overflow-y-auto bg-[#101211] text-white outline-none">
              <Dialog.Title className="sr-only">
                {t("frontpage.precision.hero.brand")}
              </Dialog.Title>
              <Dialog.Description className="sr-only">
                {t("frontpage.precision.nav.menu_description")}
              </Dialog.Description>

              <div className="mx-auto flex h-[72px] w-full max-w-[1280px] shrink-0 items-center justify-between px-4 md:px-8">
                <Dialog.Close asChild>
                  <span className="inline-flex">
                    <BrandLockup inverted />
                  </span>
                </Dialog.Close>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label={t("frontpage.precision.nav.menu_close")}
                    className="inline-flex h-11 w-11 items-center justify-center text-white transition-colors duration-150 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    <X className="h-6 w-6" aria-hidden="true" />
                  </button>
                </Dialog.Close>
              </div>

              <div className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col px-4 pb-8 pt-6 md:px-8">
                <nav aria-label={t("common.main_navigation")} className="flex flex-col">
                  {NAV_ITEMS.map((item) => (
                    <Dialog.Close key={item.key} asChild>
                      <a
                        href={item.href}
                        onClick={() => void track("public_nav_clicked", item.placement)}
                        className="flex min-h-14 items-center border-b border-white/15 font-display text-2xl text-white transition-colors duration-150 hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                      >
                        {t(`frontpage.precision.nav.${item.key}`)}
                      </a>
                    </Dialog.Close>
                  ))}
                </nav>

                <div className="mt-auto flex flex-col gap-3 pt-10">
                  <LanguageSwitcher inverted className="self-start" />
                  <Dialog.Close asChild>
                    <Link
                      to={ROUTES.LOGIN}
                      onClick={() => void track("public_nav_clicked", "header_login")}
                      className="inline-flex h-11 items-center justify-center border border-white/25 px-5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                      {t("frontpage.precision.nav.login")}
                    </Link>
                  </Dialog.Close>
                  <Dialog.Close asChild>
                    <Link
                      to={ROUTES.SIGNUP}
                      onClick={() => void track("public_cta_clicked", "header_signup")}
                      className="inline-flex h-11 items-center justify-center bg-brand px-5 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-brand-700 hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                      {t("frontpage.precision.nav.signup")}
                    </Link>
                  </Dialog.Close>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </header>
  );
}
